<template>
  <div class="roles-settings">
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <Shield :size="20" class="me-2" />
          User Roles & Permissions
        </h5>
        <button class="btn btn-sm btn-primary" @click="loadRoles">
          <RefreshCw :size="16" class="me-1" />
          Refresh
        </button>
      </div>
      <div class="card-body">
        <div v-if="loadState.IsLoading" class="text-center py-4">
          <i class="fas fa-spinner fa-spin me-2"></i> Loading roles...
        </div>
        <div v-else>
          <p class="text-muted mb-3">
            Manage user roles and permissions for your organization. Select a user to modify their role.
          </p>

          <div class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Current Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in users" :key="user.Id">
                  <td>{{ user.Name || 'N/A' }}</td>
                  <td>{{ user.Email }}</td>
                  <td>
                    <span class="badge" :class="getRoleBadgeClass(user.Role)">
                      {{ user.Role || 'User' }}
                    </span>
                  </td>
                  <td>
                    <button 
                      class="btn btn-sm btn-outline-primary"
                      @click="editUserRole(user)"
                    >
                      <Edit :size="14" class="me-1" />
                      Edit Role
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Role Modal -->
    <kt-modal
      :visible="showRoleModal"
      title="Edit User Role"
      @dismiss="showRoleModal = false"
    >
      <template #body>
        <div v-if="selectedUser">
          <p class="mb-3">
            <strong>User:</strong> {{ selectedUser.Name || selectedUser.Email }}
          </p>
          <div class="mb-3">
            <label class="form-label">Select Role</label>
            <select class="form-select" v-model="selectedRole">
              <option value="User">User</option>
              <option value="Technician">Technician</option>
              <option value="Manager">Manager</option>
              <option value="Admin">Admin</option>
              <option value="Billing">Billing</option>
            </select>
            <small class="text-muted d-block mt-2">
              Changing roles will affect user permissions immediately.
            </small>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="d-flex justify-content-end gap-2">
          <button class="btn btn-outline-secondary" @click="showRoleModal = false">
            Cancel
          </button>
          <button class="btn btn-primary" @click="saveUserRole" :disabled="saveRoleState.IsLoading">
            <Save :size="16" class="me-1" />
            {{ saveRoleState.IsLoading ? 'Saving...' : 'Save Role' }}
          </button>
        </div>
      </template>
    </kt-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { Shield, Edit, Save, RefreshCw } from 'lucide-vue-next';
import KtModal from '@/components/UI/KtModal.vue';
import ApiService from '@/services/ApiService';
import Alert from '@/services/Alert';
import { useApiRequest } from '@/composables';

const { state: loadState, execute: executeLoad } = useApiRequest<any[]>('roles:load');
const { state: saveRoleState, execute: executeSaveRole } = useApiRequest('roles:saveRole');

const showRoleModal = ref(false);
const users = ref<any[]>([]);
const selectedUser = ref<any>(null);
const selectedRole = ref('User');

const loadRoles = async () => {
  await executeLoad(() => ApiService.getTenantUsers());
  if (loadState.value.IsError) {
    Alert.notify({ text: 'Failed to load users', type: 'error' });
    return;
  }
  users.value = [...(loadState.value.Data || [])];
};

const editUserRole = (user: any) => {
  selectedUser.value = user;
  selectedRole.value = user.Role || 'User';
  showRoleModal.value = true;
};

const saveUserRole = async () => {
  if (!selectedUser.value) return;
  await executeSaveRole(() => ApiService.updateUserRole(selectedUser.value.Id, selectedRole.value));
  if (saveRoleState.value.IsError) {
    Alert.notify({ text: 'Failed to update user role', type: 'error' });
    return;
  }
  selectedUser.value.Role = selectedRole.value;
  Alert.notify({ text: 'User role updated successfully', type: 'success' });
  showRoleModal.value = false;
  await loadRoles();
};

const getRoleBadgeClass = (role: string) => {
  const roleMap: Record<string, string> = {
    'Admin': 'bg-danger',
    'Manager': 'bg-warning',
    'Billing': 'bg-info',
    'Technician': 'bg-success',
    'User': 'bg-secondary'
  };
  return roleMap[role] || 'bg-secondary';
};

onMounted(() => {
  loadRoles();
});
</script>
